import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon } from '@hero/web-icons';
import type { Agreement } from '@hero/schema';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../../ui/Sheet';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { useCurrentUser } from '@hero/hooks-shared';
import { AgreementSummaryTab } from './AgreementSummaryTab';
import { AgreementRoutesTab } from './AgreementRoutesTab';
import { AgreementEditDrawer } from './AgreementEditDrawer';
import { getAgreementCounterparty } from '../utils';

export interface AgreementDrawerProps {
  agreement: Agreement | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AgreementDrawer({ agreement, isOpen, onClose }: AgreementDrawerProps) {
  const { t } = useTranslation(['m-agreement']);
  const { orgId } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<'summary' | 'routes'>('summary');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const tabs = useMemo(
    () => [
      { id: 'summary', label: t('drawer.tabs.summary') },
      { id: 'routes', label: t('drawer.tabs.routes') },
    ],
    [t],
  );

  const actions: ActionButtonGroup[] = useMemo(
    () => [
      {
        id: 'edit',
        label: t('drawer.edit'),
        description: t('drawer.edit_description'),
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => setIsEditOpen(true),
      },
    ],
    [t],
  );

  const counterpartyName = agreement
    ? getAgreementCounterparty(agreement, orgId)?.name ?? ''
    : '';

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>{t('drawer.title')}</SheetTitle>
            <SheetDescription>{counterpartyName}</SheetDescription>
          </SheetHeader>

          {agreement && (
            <>
              <DrawerHeader
                title={counterpartyName}
                subtitle={t(`type.${agreement.type}`, { defaultValue: agreement.type })}
                onClose={onClose}
              />
              <DrawerTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={tab => setActiveTab(tab as 'summary' | 'routes')}
              />
              <DrawerContent>
                {activeTab === 'summary' ? (
                  <AgreementSummaryTab agreement={agreement} />
                ) : (
                  <AgreementRoutesTab
                    agreement={agreement}
                    onCloseDrawer={onClose}
                  />
                )}
              </DrawerContent>
              <DrawerFooter actions={actions} buttonLabel={t('drawer.actions')} />
            </>
          )}
        </SheetContent>
      </Sheet>

      {agreement && (
        <AgreementEditDrawer
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          agreementId={agreement._id}
        />
      )}
    </>
  );
}
